import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { radioStations, type RadioStation } from "@/mocks/radios";
import { programs, type Program } from "@/mocks/programs";
import { canonicalPersons, type CanonicalPerson } from "@/mocks/persons";
import { clips, type Clip } from "@/mocks/clips";
import { adminEvents, type RadioEvent } from "@/mocks/adminEvents";
import { adminContests, type Contest } from "@/mocks/adminContests";
import { useAudioPlayer } from "@/context/AudioContext";

// ── Tipos de resultado ──
type ContentKind = "radio" | "program" | "host" | "video" | "podcast" | "event" | "contest";

interface SearchResult {
  kind: ContentKind;
  data: RadioStation | Program | CanonicalPerson | Clip | RadioEvent | Contest;
}

interface FilterState {
  contentTypes: ContentKind[];
  genre: string | null;
  country: string | null;
  language: string | null;
  liveOnly: boolean;
  featuredOnly: boolean;
}

const ALL_KINDS: ContentKind[] = ["radio", "program", "host", "video", "podcast", "event", "contest"];

const KIND_LABELS: Record<ContentKind, { label: string; icon: string; color: string }> = {
  radio:    { label: "Radios",      icon: "ri-radio-line",           color: "bg-red-500/15 text-red-400 border-red-500/30" },
  program:  { label: "Programas",   icon: "ri-mic-line",             color: "bg-emerald-500/15 text-emerald-400 border-emerald-500/30" },
  host:     { label: "Locutores",   icon: "ri-user-voice-line",      color: "bg-sky-500/15 text-sky-400 border-sky-500/30" },
  video:    { label: "Videos",      icon: "ri-film-line",            color: "bg-amber-500/15 text-amber-400 border-amber-500/30" },
  podcast:  { label: "Podcasts",    icon: "ri-headphone-line",       color: "bg-violet-500/15 text-violet-400 border-violet-500/30" },
  event:    { label: "Eventos",     icon: "ri-calendar-event-line",  color: "bg-orange-500/15 text-orange-400 border-orange-500/30" },
  contest:  { label: "Concursos",   icon: "ri-trophy-line",          color: "bg-fuchsia-500/15 text-fuchsia-400 border-fuchsia-500/30" },
};

interface Props {
  open: boolean;
  onClose: () => void;
  saveStateAndNavigate: (path: string) => void;
}

export default function TvSearchOverlay({ open, onClose, saveStateAndNavigate }: Props) {
  const { play } = useAudioPlayer();
  const [query, setQuery] = useState("");
  const [focusIdx, setFocusIdx] = useState(0);
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    contentTypes: [...ALL_KINDS],
    genre: null,
    country: null,
    language: null,
    liveOnly: false,
    featuredOnly: false,
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsContainerRef = useRef<HTMLDivElement>(null);
  const filterRowRef = useRef<HTMLDivElement>(null);

  // ── Reset al abrir ──
  useEffect(() => {
    if (open) {
      setQuery("");
      setFocusIdx(0);
      setShowFilters(false);
      setFilters({
        contentTypes: [...ALL_KINDS],
        genre: null,
        country: null,
        language: null,
        liveOnly: false,
        featuredOnly: false,
      });
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [open]);

  // ── Colecciones únicas para filtros ──
  const availableGenres = useMemo(() => {
    const cats = new Set<string>();
    radioStations.forEach(r => cats.add(r.category));
    programs.forEach(p => cats.add(p.category));
    return [...cats].sort();
  }, []);

  const availableCountries = useMemo(() => {
    const countries = new Set<string>();
    radioStations.forEach(r => countries.add(r.country));
    return [...countries].sort();
  }, []);

  const availableLanguages = useMemo(() => {
    const langs = new Set<string>();
    radioStations.forEach(r => langs.add(r.language));
    return [...langs].sort();
  }, []);

  // ── Búsqueda principal ──
  const allResults = useMemo((): SearchResult[] => {
    const term = query.toLowerCase().trim();
    if (!term) return [];
    const results: SearchResult[] = [];

    // Radios
    if (filters.contentTypes.includes("radio")) {
      radioStations
        .filter(r => {
          if (filters.genre && r.category !== filters.genre) return false;
          if (filters.country && r.country !== filters.country) return false;
          if (filters.language && r.language !== filters.language) return false;
          if (filters.liveOnly && !r.isLive) return false;
          if (filters.featuredOnly && !r.isTopRadio) return false;
          return (
            r.name.toLowerCase().includes(term) ||
            r.frequency.toLowerCase().includes(term) ||
            r.category.toLowerCase().includes(term) ||
            r.city.toLowerCase().includes(term) ||
            r.hostName.toLowerCase().includes(term) ||
            r.country.toLowerCase().includes(term) ||
            r.language.toLowerCase().includes(term)
          );
        })
        .forEach(r => results.push({ kind: "radio", data: r }));
    }

    // Programas
    if (filters.contentTypes.includes("program")) {
      programs
        .filter(p => {
          if (filters.genre && p.category !== filters.genre) return false;
          if (filters.liveOnly && !p.isActive) return false;
          return (
            p.name.toLowerCase().includes(term) ||
            p.hosts.some(h => h.name.toLowerCase().includes(term)) ||
            p.category.toLowerCase().includes(term) ||
            p.radioName.toLowerCase().includes(term) ||
            p.tags.some(t => t.toLowerCase().includes(term))
          );
        })
        .forEach(p => results.push({ kind: "program", data: p }));
    }

    // Locutores (Hosts)
    if (filters.contentTypes.includes("host")) {
      canonicalPersons
        .filter(p => {
          if (filters.liveOnly && p.hostProfile?.hostStatus !== "on_air") return false;
          return (
            p.displayName.toLowerCase().includes(term) ||
            p.bio?.toLowerCase().includes(term) ||
            p.hostProfile?.programs.some(pr => pr.toLowerCase().includes(term)) ||
            p.legacyAliases.some(a => a.toLowerCase().includes(term))
          );
        })
        .forEach(p => results.push({ kind: "host", data: p }));
    }

    // Videos (clips)
    if (filters.contentTypes.includes("video")) {
      clips
        .filter(c => {
          if (filters.featuredOnly && !c.esTendencia) return false;
          return (
            c.titulo.toLowerCase().includes(term) ||
            c.tags.some(t => t.toLowerCase().includes(term)) ||
            c.radioNombre.toLowerCase().includes(term) ||
            (c.locutorNombre || "").toLowerCase().includes(term) ||
            c.descripcion.toLowerCase().includes(term)
          );
        })
        .forEach(c => results.push({ kind: "video", data: c }));
    }

    // Podcasts (programs again, treated as podcasts in TV)
    if (filters.contentTypes.includes("podcast")) {
      programs
        .filter(p => {
          if (filters.genre && p.category !== filters.genre) return false;
          return (
            p.name.toLowerCase().includes(term) ||
            p.hosts.some(h => h.name.toLowerCase().includes(term)) ||
            p.tags.some(t => t.toLowerCase().includes(term)) ||
            p.description.toLowerCase().includes(term)
          );
        })
        .forEach(p => results.push({ kind: "podcast", data: p }));
    }

    // Eventos
    if (filters.contentTypes.includes("event")) {
      adminEvents
        .filter(e => {
          if (filters.featuredOnly && !e.highlighted) return false;
          if (filters.liveOnly && e.status !== "publicado") return false;
          return (
            e.title.toLowerCase().includes(term) ||
            e.description.toLowerCase().includes(term) ||
            e.location.toLowerCase().includes(term) ||
            e.type.toLowerCase().includes(term)
          );
        })
        .forEach(e => results.push({ kind: "event", data: e }));
    }

    // Concursos
    if (filters.contentTypes.includes("contest")) {
      adminContests
        .filter(c => {
          if (filters.liveOnly && c.status !== "activo") return false;
          return (
            c.title.toLowerCase().includes(term) ||
            c.description.toLowerCase().includes(term) ||
            c.prize.toLowerCase().includes(term) ||
            c.rules.toLowerCase().includes(term)
          );
        })
        .forEach(c => results.push({ kind: "contest", data: c }));
    }

    return results.slice(0, 50);
  }, [query, filters]);

  // ── Agrupar resultados ──
  const groupedResults = useMemo(() => {
    const groups: { kind: ContentKind; label: string; icon: string; color: string; items: SearchResult[] }[] = [];
    const order: ContentKind[] = ["radio", "program", "host", "video", "podcast", "event", "contest"];
    for (const kind of order) {
      const items = allResults.filter(r => r.kind === kind);
      if (items.length > 0) {
        const info = KIND_LABELS[kind];
        groups.push({ kind, label: info.label, icon: info.icon, color: info.color, items });
      }
    }
    return groups;
  }, [allResults]);

  // ── Lista plana para navegación con foco ──
  const flatItems = useMemo(() => {
    const items: { type: "group" | "result"; groupLabel?: string; result?: SearchResult; groupIdx?: number }[] = [];
    groupedResults.forEach((group, gi) => {
      items.push({ type: "group", groupLabel: group.label, groupIdx: gi });
      group.items.forEach(item => {
        items.push({ type: "result", result: item, groupIdx: gi });
      });
    });
    return items;
  }, [groupedResults]);

  // ── Navegación con teclas ──
  const handleKey = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setFocusIdx(i => Math.min(i + 1, flatItems.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setFocusIdx(i => Math.max(i - 1, 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const item = flatItems[focusIdx];
      if (!item || item.type !== "result" || !item.result) return;
      const res = item.result;
      if (res.kind === "radio") {
        const radio = res.data as RadioStation;
        play(radio);
        onClose();
      } else if (res.kind === "program") {
        const prog = res.data as Program;
        saveStateAndNavigate(`/tv-app/program/${prog.slug}`);
        onClose();
      } else if (res.kind === "host") {
        const host = res.data as CanonicalPerson;
        saveStateAndNavigate(`/tv-app/host/${host.id}`);
        onClose();
      } else if (res.kind === "video") {
        const clip = res.data as Clip;
        saveStateAndNavigate(`/tv-app/video/${clip.id}`);
        onClose();
      } else if (res.kind === "podcast") {
        const prog = res.data as Program;
        saveStateAndNavigate(`/tv-app/podcast/${prog.id}`);
        onClose();
      } else if (res.kind === "event") {
        const evt = res.data as RadioEvent;
        saveStateAndNavigate(`/tv-app/event/${evt.id}`);
        onClose();
      } else if (res.kind === "contest") {
        const contest = res.data as Contest;
        saveStateAndNavigate(`/tv-app/contest/${contest.id}`);
        onClose();
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      if (showFilters) {
        setShowFilters(false);
      } else {
        onClose();
      }
    } else if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
      // Navegación horizontal en filtros si están visibles
      if (showFilters && filterRowRef.current) {
        // dejamos que el focus natural maneje esto
      }
    }
  }, [flatItems, focusIdx, play, onClose, saveStateAndNavigate, showFilters]);

  // ── Auto-scroll al resultado enfocado ──
  useEffect(() => {
    if (resultsContainerRef.current) {
      const el = resultsContainerRef.current.querySelector(`[data-search-idx="${focusIdx}"]`);
      if (el) {
        el.scrollIntoView({ block: "nearest", behavior: "smooth" });
      }
    }
  }, [focusIdx]);

  // ── Toggle content type filter ──
  const toggleContentType = (kind: ContentKind) => {
    setFilters(prev => {
      const types = prev.contentTypes.includes(kind)
        ? prev.contentTypes.filter(t => t !== kind)
        : [...prev.contentTypes, kind];
      return { ...prev, contentTypes: types.length === 0 ? [...ALL_KINDS] : types };
    });
    setFocusIdx(0);
  };

  // ── Set single-select filter ──
  const setGenre = (g: string | null) => { setFilters(prev => ({ ...prev, genre: prev.genre === g ? null : g })); setFocusIdx(0); };
  const setCountry = (c: string | null) => { setFilters(prev => ({ ...prev, country: prev.country === c ? null : c })); setFocusIdx(0); };
  const setLanguage = (l: string | null) => { setFilters(prev => ({ ...prev, language: prev.language === l ? null : l })); setFocusIdx(0); };
  const toggleLive = () => { setFilters(prev => ({ ...prev, liveOnly: !prev.liveOnly })); setFocusIdx(0); };
  const toggleFeatured = () => { setFilters(prev => ({ ...prev, featuredOnly: !prev.featuredOnly })); setFocusIdx(0); };

  // ── Contar filtros activos ──
  const activeFilterCount = [
    filters.genre ? 1 : 0,
    filters.country ? 1 : 0,
    filters.language ? 1 : 0,
    filters.liveOnly ? 1 : 0,
    filters.featuredOnly ? 1 : 0,
    filters.contentTypes.length < ALL_KINDS.length ? 1 : 0,
  ].reduce((a, b) => a + b, 0);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center pt-20 bg-foreground-950/80 backdrop-blur-sm">
      <div
        className="w-full max-w-4xl bg-[#0f0f15] border border-white/10 rounded-3xl overflow-hidden shadow-2xl max-h-[85vh] flex flex-col"
        onKeyDown={handleKey}
      >
        {/* ── Search input ── */}
        <div className="flex items-center gap-4 px-6 py-5 border-b border-white/5 shrink-0">
          <i className="ri-search-line text-white/40 text-xl"></i>
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={e => { setQuery(e.target.value); setFocusIdx(0); }}
            placeholder="Buscar radio, programa, locutor, video, evento, concurso..."
            className="flex-1 bg-transparent text-white text-lg placeholder:text-white/30 outline-none"
          />
          {query && (
            <button
              onClick={() => { setQuery(""); setFocusIdx(0); inputRef.current?.focus(); }}
              className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/15 flex items-center justify-center cursor-pointer transition-all"
            >
              <i className="ri-close-line text-white/60 text-sm"></i>
            </button>
          )}
          {/* Filter toggle button */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-all ${
              showFilters || activeFilterCount > 0
                ? "bg-white/15 text-white"
                : "bg-white/5 text-white/50 hover:text-white/80 hover:bg-white/10"
            }`}
          >
            <i className="ri-filter-3-line"></i>
            Filtros
            {activeFilterCount > 0 && (
              <span className="w-5 h-5 rounded-full bg-accent-400 text-[#0a0a0f] text-[10px] font-bold flex items-center justify-center">
                {activeFilterCount}
              </span>
            )}
          </button>
          <button
            onClick={onClose}
            className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/15 text-white/70 text-sm font-semibold cursor-pointer whitespace-nowrap transition-all"
          >
            <i className="ri-keyboard-line"></i>
            Esc
          </button>
        </div>

        {/* ── Filter panel ── */}
        {showFilters && (
          <div ref={filterRowRef} className="px-6 py-4 border-b border-white/5 space-y-3 shrink-0 max-h-[220px] overflow-y-auto">
            {/* Row 1: Content types */}
            <div>
              <p className="text-[10px] font-bold text-white/25 uppercase tracking-widest mb-2">Tipo de contenido</p>
              <div className="flex flex-wrap gap-1.5">
                {ALL_KINDS.map(kind => {
                  const info = KIND_LABELS[kind];
                  const active = filters.contentTypes.includes(kind);
                  return (
                    <button
                      key={kind}
                      onClick={() => toggleContentType(kind)}
                      className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border cursor-pointer whitespace-nowrap transition-all ${
                        active
                          ? `${info.color}`
                          : "border-white/5 text-white/30 hover:text-white/50 hover:border-white/10"
                      }`}
                    >
                      <i className={`${info.icon} text-[11px]`}></i>
                      {info.label}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Row 2: Genre, Country, Language */}
            <div className="flex flex-wrap gap-4">
              {/* Genre */}
              <div className="flex-1 min-w-[160px]">
                <p className="text-[10px] font-bold text-white/25 uppercase tracking-widest mb-1.5">Género</p>
                <div className="flex flex-wrap gap-1">
                  {availableGenres.slice(0, 8).map(g => (
                    <button
                      key={g}
                      onClick={() => setGenre(g)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border cursor-pointer whitespace-nowrap transition-all ${
                        filters.genre === g
                          ? "bg-white/15 text-white border-white/20"
                          : "border-white/5 text-white/35 hover:text-white/60 hover:border-white/10"
                      }`}
                    >
                      {g}
                    </button>
                  ))}
                </div>
              </div>

              {/* Country */}
              <div className="flex-1 min-w-[140px]">
                <p className="text-[10px] font-bold text-white/25 uppercase tracking-widest mb-1.5">País</p>
                <div className="flex flex-wrap gap-1">
                  {availableCountries.map(c => (
                    <button
                      key={c}
                      onClick={() => setCountry(c)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border cursor-pointer whitespace-nowrap transition-all ${
                        filters.country === c
                          ? "bg-white/15 text-white border-white/20"
                          : "border-white/5 text-white/35 hover:text-white/60 hover:border-white/10"
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>
              </div>

              {/* Language */}
              <div className="flex-1 min-w-[120px]">
                <p className="text-[10px] font-bold text-white/25 uppercase tracking-widest mb-1.5">Idioma</p>
                <div className="flex flex-wrap gap-1">
                  {availableLanguages.map(l => (
                    <button
                      key={l}
                      onClick={() => setLanguage(l)}
                      className={`px-2.5 py-1 rounded-full text-[10px] font-semibold border cursor-pointer whitespace-nowrap transition-all ${
                        filters.language === l
                          ? "bg-white/15 text-white border-white/20"
                          : "border-white/5 text-white/35 hover:text-white/60 hover:border-white/10"
                      }`}
                    >
                      {l}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Row 3: Toggle filters */}
            <div className="flex items-center gap-3">
              <button
                onClick={toggleLive}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border cursor-pointer whitespace-nowrap transition-all ${
                  filters.liveOnly
                    ? "bg-red-500/15 text-red-400 border-red-500/30"
                    : "border-white/5 text-white/35 hover:text-white/60 hover:border-white/10"
                }`}
              >
                <span className={`w-1.5 h-1.5 rounded-full ${filters.liveOnly ? "bg-red-500 animate-pulse" : "bg-white/20"}`}></span>
                Solo en vivo
              </button>
              <button
                onClick={toggleFeatured}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-bold border cursor-pointer whitespace-nowrap transition-all ${
                  filters.featuredOnly
                    ? "bg-amber-500/15 text-amber-400 border-amber-500/30"
                    : "border-white/5 text-white/35 hover:text-white/60 hover:border-white/10"
                }`}
              >
                <i className="ri-star-fill text-[11px]"></i>
                Destacados
              </button>

              {/* Reset filters */}
              {activeFilterCount > 0 && (
                <button
                  onClick={() => {
                    setFilters({
                      contentTypes: [...ALL_KINDS],
                      genre: null,
                      country: null,
                      language: null,
                      liveOnly: false,
                      featuredOnly: false,
                    });
                    setFocusIdx(0);
                  }}
                  className="flex items-center gap-1 px-3 py-1.5 rounded-full text-[11px] font-semibold text-white/40 hover:text-white/70 border border-white/5 hover:border-white/10 cursor-pointer whitespace-nowrap transition-all"
                >
                  <i className="ri-refresh-line"></i>
                  Limpiar filtros
                </button>
              )}
            </div>
          </div>
        )}

        {/* ── Results ── */}
        <div ref={resultsContainerRef} className="flex-1 overflow-y-auto p-4 space-y-1 min-h-0">
          {/* Empty states */}
          {allResults.length === 0 && query && (
            <div className="text-center py-14">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                <i className="ri-search-line text-white/25 text-2xl"></i>
              </div>
              <p className="text-white/40 text-lg">Sin resultados para "{query}"</p>
              <p className="text-white/25 text-sm mt-1.5">
                {activeFilterCount > 0 ? "Probá quitando filtros o usando otro término" : "Probá con otro término"}
              </p>
              {activeFilterCount > 0 && (
                <button
                  onClick={() => {
                    setFilters({
                      contentTypes: [...ALL_KINDS],
                      genre: null,
                      country: null,
                      language: null,
                      liveOnly: false,
                      featuredOnly: false,
                    });
                    setFocusIdx(0);
                  }}
                  className="mt-4 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/15 text-white text-xs font-semibold cursor-pointer whitespace-nowrap transition-all"
                >
                  Limpiar filtros y buscar de nuevo
                </button>
              )}
            </div>
          )}

          {allResults.length === 0 && !query && (
            <div className="text-center py-14">
              <div className="w-16 h-16 mx-auto rounded-2xl bg-white/5 flex items-center justify-center mb-4">
                <i className="ri-keyboard-line text-white/25 text-2xl"></i>
              </div>
              <p className="text-white/40 text-lg">Escribí para buscar</p>
              <p className="text-white/25 text-sm mt-1.5">Radios, programas, locutores, videos, eventos, concursos</p>
            </div>
          )}

          {/* Grouped results */}
          {groupedResults.map((group, gi) => {
            const info = KIND_LABELS[group.kind];
            return (
              <div key={group.kind} className="mb-3">
                {/* Group header */}
                <div className="flex items-center gap-3 px-3 py-2 sticky top-0 bg-[#0f0f15]/95 backdrop-blur-sm z-10 rounded-xl">
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center ${info.color.split(" ")[0]}`}>
                    <i className={`${info.icon} text-xs ${info.color.split(" ")[1]}`}></i>
                  </div>
                  <span className="text-sm font-bold text-white">{group.label}</span>
                  <span className="text-xs text-white/30">{group.items.length}</span>
                </div>

                {/* Group items */}
                <div className="space-y-1 mt-1">
                  {group.items.map((item) => {
                    const globalIdx = flatItems.findIndex(
                      fi => fi.type === "result" && fi.result?.kind === item.kind && fi.result?.data === item.data
                    );
                    const isFocused = globalIdx === focusIdx;

                    if (item.kind === "radio") {
                      const r = item.data as RadioStation;
                      return (
                        <div
                          key={`radio-${r.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { play(r); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <img src={r.logoUrl} alt={r.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{r.name}</h3>
                              {r.isLive && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/20 text-red-400 text-[10px] font-bold whitespace-nowrap">
                                  <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                                  EN VIVO
                                </span>
                              )}
                              {r.isTopRadio && (
                                <span className="text-amber-400 text-xs"><i className="ri-star-fill"></i></span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {r.frequency} · {r.category} · {r.city}, {r.country} · {r.language}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-accent-400 text-sm font-semibold">
                              <i className="ri-play-circle-line"></i>
                              <span>Sintonizar</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "program") {
                      const p = item.data as Program;
                      return (
                        <div
                          key={`program-${p.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/program/${p.slug}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <img src={p.coverUrl} alt={p.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{p.name}</h3>
                              {p.isActive && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-400 text-[10px] font-bold whitespace-nowrap">
                                  ACTIVO
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {p.hosts[0]?.name || "Varios"} · {p.radioName} · {p.schedule}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-white/40 text-sm font-semibold">
                              <i className="ri-arrow-right-line"></i>
                              <span>Ver</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "host") {
                      const h = item.data as CanonicalPerson;
                      const onAir = h.hostProfile?.hostStatus === "on_air";
                      return (
                        <div
                          key={`host-${h.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/host/${h.id}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <img src={h.avatar} alt={h.displayName} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{h.displayName}</h3>
                              {onAir && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-sky-500/20 text-sky-400 text-[10px] font-bold whitespace-nowrap">
                                  <span className="w-1.5 h-1.5 rounded-full bg-sky-500 animate-pulse"></span>
                                  AL AIRE
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              @{h.hostProfile?.username?.replace("@", "") || h.displayName.toLowerCase()} · {h.hostProfile?.programs[0] || h.bio?.split(".")[0] || ""}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-white/40 text-sm font-semibold">
                              <i className="ri-user-line"></i>
                              <span>Perfil</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "video") {
                      const c = item.data as Clip;
                      const durLabels: Record<string, string> = { "15": "15s", "30": "30s", "60": "1m", "180": "3m" };
                      return (
                        <div
                          key={`video-${c.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/video/${c.id}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0 relative">
                            <img src={c.imagenUrl} alt={c.titulo} className="w-full h-full object-cover" />
                            <div className="absolute inset-0 flex items-center justify-center bg-foreground-950/40">
                              <i className="ri-play-fill text-white text-lg"></i>
                            </div>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{c.titulo}</h3>
                              {c.esTendencia && (
                                <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-400 text-[10px] font-bold whitespace-nowrap">
                                  <i className="ri-fire-line"></i>
                                  TENDENCIA
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {c.radioNombre} · {c.locutorNombre || ""} · {durLabels[c.duracion] || c.duracion}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-amber-400 text-sm font-semibold">
                              <i className="ri-play-circle-line"></i>
                              <span>Reproducir</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "podcast") {
                      const p = item.data as Program;
                      return (
                        <div
                          key={`podcast-${p.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/podcast/${p.id}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <img src={p.coverUrl} alt={p.name} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{p.name}</h3>
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {p.hosts[0]?.name || "Varios"} · {p.radioName}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-violet-400 text-sm font-semibold">
                              <i className="ri-headphone-line"></i>
                              <span>Escuchar</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "event") {
                      const ev = item.data as RadioEvent;
                      const date = new Date(ev.date);
                      const formattedDate = date.toLocaleDateString("es-ES", { weekday: "short", day: "numeric", month: "short" });
                      const typeLabel = ev.type === "concierto" ? "Concierto" : ev.type === "transmision_especial" ? "Transmisión" : ev.type;
                      return (
                        <div
                          key={`event-${ev.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/event/${ev.id}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl overflow-hidden bg-white/10 flex-shrink-0">
                            <img src={ev.image} alt={ev.title} className="w-full h-full object-cover" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{ev.title}</h3>
                              {ev.status === "publicado" && (
                                <span className="px-2 py-0.5 rounded-full bg-amber-500/20 text-amber-400 text-[10px] font-bold whitespace-nowrap">
                                  {typeLabel}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {formattedDate} · {ev.time} · {ev.location}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-white/40 text-sm font-semibold">
                              <i className="ri-calendar-event-line"></i>
                              <span>Ver</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    if (item.kind === "contest") {
                      const ct = item.data as Contest;
                      return (
                        <div
                          key={`contest-${ct.id}`}
                          data-search-idx={globalIdx}
                          tabIndex={0}
                          onClick={() => { saveStateAndNavigate(`/tv-app/contest/${ct.id}`); onClose(); }}
                          onFocus={() => setFocusIdx(globalIdx)}
                          className={`flex items-center gap-4 px-5 py-4 rounded-2xl cursor-pointer transition-all outline-none ${
                            isFocused ? "bg-white/15 ring-2 ring-white/30" : "bg-white/5 hover:bg-white/10"
                          }`}
                        >
                          <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-violet-500/20 to-fuchsia-500/10 flex items-center justify-center flex-shrink-0">
                            <i className="ri-trophy-line text-violet-400 text-xl"></i>
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2">
                              <h3 className="text-lg font-bold text-white truncate">{ct.title}</h3>
                              <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap ${
                                ct.status === "activo"
                                  ? "bg-emerald-500/20 text-emerald-400"
                                  : ct.status === "proximamente"
                                  ? "bg-amber-500/20 text-amber-400"
                                  : "bg-white/10 text-white/40"
                              }`}>
                                {ct.status === "activo" ? "ACTIVO" : ct.status === "proximamente" ? "PRÓXIMO" : ct.status.toUpperCase()}
                              </span>
                            </div>
                            <p className="text-sm text-white/50 truncate mt-0.5">
                              {ct.prize.length > 40 ? ct.prize.slice(0, 40) + "…" : ct.prize}
                            </p>
                          </div>
                          <div className="flex-shrink-0">
                            <div className="flex items-center gap-1.5 text-fuchsia-400 text-sm font-semibold">
                              <i className="ri-gift-line"></i>
                              <span>Ver</span>
                            </div>
                          </div>
                        </div>
                      );
                    }

                    return null;
                  })}
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Footer hints ── */}
        <div className="flex items-center gap-6 px-6 py-3 border-t border-white/5 text-[11px] text-white/25 shrink-0">
          <span className="flex items-center gap-1.5">
            <i className="ri-arrow-up-down-line"></i>
            Navegar
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-keyboard-line"></i>
            Enter: Abrir
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-filter-3-line"></i>
            Filtros
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-close-line"></i>
            Esc: Cerrar
          </span>
          {allResults.length > 0 && (
            <span className="ml-auto text-white/35">
              {allResults.length} resultados · {groupedResults.length} grupos
            </span>
          )}
        </div>
      </div>
    </div>
  );
}